'use client';

// Inline chart renderer for agent messages.
//
// The agent is instructed (via prompt rule) to emit analytical comparisons
// as a fenced ```chart-json block. AgentChat.tsx detects the fence,
// JSON-parses the payload, and hands it here.
//
// Schema (kept intentionally small — wider schema = more for the model to
// get wrong):
//   {
//     "type": "bar" | "line" | "pie",
//     "title"?: string,
//     "data": Array<Record<string, string | number>>,
//     "xKey": string,                 // category axis (bar/line)
//     "series": string | string[],    // value key(s); array → grouped bars
//     "format"?: "currency" | "number" | "percent",
//     "footer"?: string,
//   }

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const SERIES_COLORS = ['#00C2A8', '#0891B2', '#7C3AED', '#EC4899', '#F59E0B', '#10B981', '#6366F1'];

function fmt(value: number, format?: string): string {
  if (typeof value !== 'number') return String(value);
  if (format === 'currency') return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (format === 'percent') return value.toFixed(1) + '%';
  return value.toLocaleString('en-US');
}

interface ChartPayload {
  type: 'bar' | 'line' | 'pie';
  title?: string;
  data: Array<Record<string, string | number>>;
  xKey?: string;
  series?: string | string[];
  format?: 'currency' | 'number' | 'percent';
  footer?: string;
}

export function ChartBlock({ payload }: { payload: ChartPayload }) {
  if (!payload || !Array.isArray(payload.data) || payload.data.length === 0) {
    return null;
  }

  const series = Array.isArray(payload.series) ? payload.series : payload.series ? [payload.series] : [];
  const xKey = payload.xKey || 'name';
  const format = payload.format;

  // Recharts 3.x's Formatter<ValueType, NameType> takes 5 args. We only
  // care about value; widen the signature with `any` to keep TS quiet
  // without dragging in recharts' generic types.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tooltipFormatter: any = (value: number | string) =>
    typeof value === 'number' ? fmt(value, format) : value;

  const yTickFormatter = (value: number) => fmt(value, format);

  return (
    <div className="my-3 rounded-lg border border-gray-200 bg-white p-3">
      {payload.title ? (
        <div className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-2">
          {payload.title}
        </div>
      ) : null}
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          {payload.type === 'pie' ? (
            <PieChart>
              <Pie
                data={payload.data}
                dataKey={(typeof series[0] === 'string' && series[0]) || 'value'}
                nameKey={xKey}
                outerRadius={80}
                label={(entry: { name?: string; value?: number }) =>
                  `${entry.name ?? ''}: ${fmt(entry.value ?? 0, format)}`
                }
              >
                {payload.data.map((_, i) => (
                  <Cell key={i} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={tooltipFormatter} />
            </PieChart>
          ) : payload.type === 'line' ? (
            <LineChart data={payload.data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={yTickFormatter} />
              <Tooltip formatter={tooltipFormatter} />
              {series.length > 1 ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
              {series.map((key, i) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ))}
            </LineChart>
          ) : (
            <BarChart data={payload.data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={yTickFormatter} />
              <Tooltip formatter={tooltipFormatter} />
              {series.length > 1 ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
              {series.map((key, i) => (
                <Bar key={key} dataKey={key} fill={SERIES_COLORS[i % SERIES_COLORS.length]} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      {payload.footer ? (
        <div className="text-[10px] text-gray-500 mt-2 italic">{payload.footer}</div>
      ) : null}
    </div>
  );
}

// Extracts and parses chart-json fenced blocks from agent text.
// Returns a list of { match, payload } so the caller can replace the
// fence range with a rendered chart while keeping surrounding markdown.
export interface ChartFenceMatch {
  start: number;
  end: number;
  payload: ChartPayload | null;
}
export function extractChartFences(text: string): ChartFenceMatch[] {
  if (!text) return [];
  const out: ChartFenceMatch[] = [];
  // Match ```chart-json ... ```  (capture the json body)
  const re = /```chart-json\s*([\s\S]*?)```/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    let payload: ChartPayload | null = null;
    try {
      payload = JSON.parse(m[1].trim());
    } catch {
      payload = null;
    }
    out.push({ start: m.index, end: m.index + m[0].length, payload });
  }
  return out;
}
